import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { VendorService } from '@/services/vendor.service';
import { InvoiceStatus, PurchaseOrderStatus } from '@/models/vendor.model';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { Building2, ArrowLeft, Edit, Trash2, Mail, Phone, DollarSign, FileText, Package, Plus, Star } from 'lucide-react';

const getStatusVariant = (status: string): "success" | "warning" | "danger" | "gray" | "info" => {
  const variants: Record<string, "success" | "warning" | "danger" | "gray" | "info"> = {
    active: 'success',
    inactive: 'gray',
    pending: 'warning',
    suspended: 'danger',
    draft: 'gray',
    approved: 'info',
    sent: 'info',
    received: 'success',
    cancelled: 'gray',
    paid: 'success',
    overdue: 'danger',
  };
  return variants[status] || 'gray';
};

export const VendorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: vendor, isLoading, error } = useQuery({
    queryKey: ['vendor', id],
    queryFn: () => (id ? VendorService.getVendorById(id) : null),
    enabled: Boolean(id),
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ['vendorInvoicesByVendor', id],
    queryFn: () => (id ? VendorService.getInvoicesByVendor(id) : Promise.resolve([])),
    enabled: Boolean(vendor) && Boolean(id),
  });

  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['purchaseOrdersByVendor', id],
    queryFn: () => (id ? VendorService.getPurchaseOrdersByVendor(id) : Promise.resolve([])),
    enabled: Boolean(vendor) && Boolean(id),
  });

  const vendorInvoices = useMemo(() => invoices, [invoices]);
  const vendorPOs = useMemo(() => purchaseOrders, [purchaseOrders]);

  const lifetimeSpend = useMemo(
    () => vendorInvoices.filter((i) => i.status === InvoiceStatus.paid).reduce((sum, i) => sum + i.total, 0),
    [vendorInvoices]
  );
  const openInvoiceCount = useMemo(
    () =>
      vendorInvoices.filter((i) => i.status === InvoiceStatus.pending || i.status === InvoiceStatus.approved || i.status === InvoiceStatus.overdue)
        .length,
    [vendorInvoices]
  );
  const openPOCount = useMemo(
    () =>
      vendorPOs.filter(
        (po) =>
          po.status === PurchaseOrderStatus.draft ||
          po.status === PurchaseOrderStatus.pending ||
          po.status === PurchaseOrderStatus.approved ||
          po.status === PurchaseOrderStatus.sent
      ).length,
    [vendorPOs]
  );

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await VendorService.deleteVendor(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      navigate('/vendors');
    },
  });

  if (isLoading) return <LoadingSpinner text="Loading vendor..." fullScreen />;

  if (error || !vendor) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={Building2}
          title={error ? "Failed to load vendor" : "Vendor not found"}
          description={error instanceof Error ? error.message : "The requested vendor does not exist or you may not have access."}
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/vendors')}>
              Back to Vendors
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/vendors')}
          className="mb-4"
        >
          Back to Vendors
        </Button>
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h1 className="text-3xl font-bold text-gray-900">{vendor.name}</h1>
                  <Badge variant={getStatusVariant(vendor.status)} size="sm">
                    {vendor.status}
                  </Badge>
                  <Badge variant="info" size="sm">{vendor.type.split('_').join(' ')}</Badge>
                  {vendor.rating && (
                    <Badge variant="warning" size="sm">
                      <Star className="w-3 h-3 mr-1" />
                      {vendor.rating}/5
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                  {vendor.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {vendor.email}
                    </span>
                  )}
                  {vendor.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {vendor.phone}
                    </span>
                  )}
                  {vendor.paymentTerms && (
                    <span>Terms: {vendor.paymentTerms}</span>
                  )}
                </div>
                {vendor.contactName && (
                  <p className="mt-2 text-sm text-gray-600">Contact: {vendor.contactName}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                size="sm"
                icon={Edit}
                onClick={() => navigate(`/vendors/${vendor.id}/edit`)}
              >
                Edit
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={Trash2}
                onClick={() => {
                  if (!id) return;
                  const ok = window.confirm('Delete this vendor? This cannot be undone.');
                  if (ok) deleteMutation.mutate();
                }}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Lifetime Spend (Paid)</p>
              <p className="text-3xl font-bold text-gray-900">${lifetimeSpend.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Open Invoices</p>
              <p className="text-3xl font-bold text-orange-600">{openInvoiceCount}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Open POs</p>
              <p className="text-3xl font-bold text-blue-600">{openPOCount}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>
      </div>

      <Card className="mb-6">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Vendor Profile</h2>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={() => navigate(`/vendors/purchase-orders/new?vendorId=${encodeURIComponent(vendor.id)}`)}
            >
              Create PO
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={() => navigate(`/vendors/invoices/new?vendorId=${encodeURIComponent(vendor.id)}`)}
            >
              Add Invoice
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-gray-500">Address</div>
            <div className="text-gray-900">
              {vendor.address ? vendor.address : '—'}
              {(vendor.city || vendor.state || vendor.zipCode) && (
                <>
                  <br />
                  {[vendor.city, vendor.state, vendor.zipCode].filter(Boolean).join(', ')}
                </>
              )}
              {vendor.country ? (
                <>
                  <br />
                  {vendor.country}
                </>
              ) : null}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Tax ID</div>
            <div className="text-gray-900">{vendor.taxId || '—'}</div>
            <div className="mt-3 text-gray-500">Website</div>
            <div className="text-gray-900">
              {vendor.website ? (
                <a href={vendor.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 break-all">
                  {vendor.website}
                </a>
              ) : (
                '—'
              )}
            </div>
          </div>
        </div>

        {vendor.tags?.length ? (
          <div className="mt-4">
            <div className="text-sm text-gray-500 mb-2">Tags</div>
            <div className="flex flex-wrap gap-2">
              {vendor.tags.map((tag) => (
                <Badge key={tag} variant="info" size="sm">{tag}</Badge>
              ))}
            </div>
          </div>
        ) : null}

        {vendor.notes ? (
          <div className="mt-4">
            <div className="text-sm text-gray-500 mb-2">Notes</div>
            <div className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{vendor.notes}</div>
          </div>
        ) : null}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Recent Invoices
            </h2>
            <Link to="/vendors" className="text-sm text-blue-600 hover:text-blue-800">
              View all
            </Link>
          </div>
          {vendorInvoices.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-8">No invoices for this vendor yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Invoice</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vendorInvoices.slice(0, 6).map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2">
                        <Link to={`/vendors/invoices/${inv.id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                          {inv.invoiceNumber || inv.id}
                        </Link>
                        <div className="text-xs text-gray-500">{inv.invoiceDate.toLocaleDateString()}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={getStatusVariant(inv.status)} size="sm">
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">${inv.total.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Recent Purchase Orders
            </h2>
            <Link to="/vendors" className="text-sm text-blue-600 hover:text-blue-800">
              View all
            </Link>
          </div>
          {vendorPOs.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-8">No purchase orders for this vendor yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">PO</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vendorPOs.slice(0, 6).map((po) => (
                    <tr key={po.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2">
                        <Link to={`/vendors/purchase-orders/${po.id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                          {po.poNumber || po.id}
                        </Link>
                        <div className="text-xs text-gray-500">{po.orderDate.toLocaleDateString()}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={getStatusVariant(po.status)} size="sm">
                          {po.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">${po.total.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

